import React, { useContext, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import { MdClose } from "react-icons/md";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../App"; // Adjust path as needed
import { Helmet } from "react-helmet-async";
import CircularProgress from "@mui/material/CircularProgress";
import TablePagination from "@mui/material/TablePagination";
import Skeleton from "@mui/material/Skeleton";
import {
  FaInfoCircle,
  FaStar,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
} from "react-icons/fa"; // Added FaChevronDown, FaChevronUp, FaDownload
import {
  setSelectedOrder,
  fetchOrders,
  cancelOrderAsync,
  fetchRefundsAsync,
} from "../../store/slices/OrdersSlice"; // Adjust path as needed
import { fetchDataFromApi } from "../../utils/api"; // Adjust path as needed
import { toast } from "react-toastify";

const Orders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const context = useContext(MyContext);

  const {
    orders = [],
    ordersPagination,
    loading: ordersLoading = true,
    refunds = [],
    refundsPagination,
    loading: refundsLoading = true,
  } = useSelector((state) => state.orders) || {};
  const selectedOrder = useSelector((state) => state.orders?.selectedOrder);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [refundsPage, setRefundsPage] = useState(0);
  const [refundsRowsPerPage, setRefundsRowsPerPage] = useState(10);

  // Filter States
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [isLast30DaysSelected, setIsLast30DaysSelected] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Actual search term for API call
  const [tempSearchTerm, setTempSearchTerm] = useState(""); // Input field value

  // State for responsive filter content visibility within sidebar
  const [isFiltersContentVisible, setIsFiltersContentVisible] = useState(false);

  // Backend enum values for order statuses
  const backendOrderStatusEnum = [
    "pending",
    "delivered",
    "cancelled",
    "confirm",
  ];
  const orderYearOptions = ["2024", "2023", "2022", "2021", "Older"];

  // Helper to map backend status to display text
  const getDisplayStatus = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "delivered":
        return "Delivered";
      case "cancelled":
        return "Cancelled";
      case "confirm":
        return "On the way"; // Mapping 'confirm' to 'On the way' for display
      case "Returned": // This might be a derived status (e.g., delivered + refund)
        return "Returned"; // Ensure backend also handles this if it's a filterable status
      case "processed": // For refund status
        return "Processed";
      case "requested": // For refund status
        return "Requested";
      default:
        return status;
    }
  };

  // Helper to get payment mode display text
  const getPaymentModeDisplay = (paymentMode) => {
    switch (paymentMode) {
      case "hybrid_cod":
        return "Hybrid COD (5% Online + 95% COD)";
      case "full_payment":
        return "Full Payment Online";
      default:
        return paymentMode || "Not specified";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    try {
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return "Invalid Date";
    }
  };

  const fetchOrderAndRefundData = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to check your orders.");
      navigate("/");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?._id) return;

    dispatch(
      fetchOrders({
        userId: user._id,
        page: ordersPage + 1,
        limit: ordersRowsPerPage,
        statuses: selectedStatuses, // These are the backend enum values
        years: selectedYears,
        last30Days: isLast30DaysSelected,
        searchTerm: searchTerm, // Use the debounced search term
      })
    );

    dispatch(
      fetchRefundsAsync({
        page: refundsPage + 1,
        limit: refundsRowsPerPage,
      })
    );

    context.setEnableFilterTab(false);
  }, [
    dispatch,
    navigate,
    context,
    ordersPage,
    ordersRowsPerPage,
    refundsPage,
    refundsRowsPerPage,
    selectedStatuses,
    selectedYears,
    isLast30DaysSelected,
    searchTerm, // Add searchTerm as a dependency
  ]);

  useEffect(() => {
    fetchOrderAndRefundData();
  }, [fetchOrderAndRefundData]);

  // Debounce effect for search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(tempSearchTerm); // Update actual searchTerm after delay
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler); // Clear timeout if tempSearchTerm changes before delay
    };
  }, [tempSearchTerm]); // Only re-run if tempSearchTerm changes

  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleYearChange = (year) => {
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
    // If a specific year is selected, uncheck 'Last 30 days' (mutually exclusive)
    if (isLast30DaysSelected) {
      setIsLast30DaysSelected(false);
    }
  };

  const handleLast30DaysChange = () => {
    setIsLast30DaysSelected((prev) => !prev);
    // If 'Last 30 days' is selected, uncheck all years (mutually exclusive)
    setSelectedYears([]);
  };

  const handleSearchClick = () => {
    // This button will directly trigger the search without debounce
    setSearchTerm(tempSearchTerm);
  };

  useEffect(() => {
    setOrdersPage(0); // Reset page to 0 when filters or search terms change
  }, [selectedStatuses, selectedYears, isLast30DaysSelected, searchTerm]);

  const handleOrdersPageChange = (event, newPage) => {
    setOrdersPage(newPage);
  };

  const handleOrdersRowsPerPageChange = (event) => {
    setOrdersRowsPerPage(parseInt(event.target.value, 10));
    setOrdersPage(0);
  };

  const handleRefundsPageChange = (event, newPage) => {
    setRefundsPage(newPage);
  };

  const handleRefundsRowsPerPageChange = (event) => {
    setRefundsRowsPerPage(parseInt(event.target.value, 10));
    setRefundsPage(0);
  };

  const handleOpenRefundModal = (refund) => {
    setSelectedRefund(refund);
    setIsRefundModalOpen(true);
  };

  const handleCloseRefundModal = () => {
    setSelectedRefund(null);
    setIsRefundModalOpen(false);
  };

  const cancelOrder = (orderId) => {
    setCancellingOrderId(orderId);
    dispatch(cancelOrderAsync(orderId))
      .unwrap()
      .then(() => {
        toast.success("Order cancelled successfully. Refreshing data...");
        fetchOrderAndRefundData();
      })
      .catch((error) => {
        toast.error(error || "Failed to cancel order.");
      })
      .finally(() => {
        setCancellingOrderId(null);
      });
  };

  const downloadInvoice = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to download invoice.");
        return;
      }

      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await fetch(
        `${baseURL}/api/orders/user/download-invoice/${orderId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Invoice not found for this order.");
        } else {
          toast.error("Failed to download invoice.");
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice. Please try again.");
    }
  };

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case "delivered":
      case "completed": // Backend 'delivered' or 'completed'
        return "bg-badge-success-bg text-badge-success-text border border-badge-success-text";
      case "cancelled":
        return "bg-badge-secondary-bg text-badge-secondary-text border border-badge-secondary-text";
      case "pending":
      case "confirm": // Backend 'pending' or 'confirm' (On the way)
        return "bg-badge-warning-bg text-badge-warning-text border border-badge-warning-text";
      case "Returned": // This is a frontend concept, assuming backend would filter on (delivered && refund:true)
        return "bg-badge-danger-bg text-badge-danger-text border border-badge-danger-text";
      case "processed": // For refund status
        return "bg-badge-success-bg text-badge-success-text border border-badge-success-text";
      case "requested": // For refund status
        return "bg-badge-warning-bg text-badge-warning-text border border-badge-warning-text";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  // Skeleton Card Component
  const OrderCardSkeleton = () => (
    <div className="border border-green-border-light rounded-lg p-4 md:p-6 shadow-sm bg-card-bg animate-pulse">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-4 mb-4 border-b border-green-border-light">
        <div className="mb-2 md:mb-0">
          <Skeleton width={180} height={24} className="rounded" />
          <Skeleton width={120} height={16} className="mt-1 rounded" />
        </div>
        <Skeleton width={80} height={24} className="rounded" />
      </div>
      <div className="space-y-4">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="flex flex-col sm:flex-row gap-4 items-center sm:items-start pb-4 last:border-b-0 last:pb-0"
          >
            <Skeleton width={80} height={80} className="rounded-md" />
            <div className="flex-grow">
              <Skeleton width="80%" height={20} className="rounded" />
              <Skeleton width="60%" height={16} className="mt-1 rounded" />
              <Skeleton width="40%" height={16} className="mt-1 rounded" />
              <Skeleton width="30%" height={24} className="mt-2 rounded" />
            </div>
            <div className="text-right flex flex-col items-end">
              <Skeleton width={150} height={16} className="rounded" />
              <Skeleton width={100} height={24} className="mt-2 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>My Orders | SabbaFarm</title>
        <meta
          name="description"
          content="Track your SabbaFarm orders easily."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://sabbafarm.com/orders" />
        <meta property="og:title" content="My Orders - SabbaFarm" />
        <meta
          property="og:description"
          content="Track your SabbaFarm order details."
        />
        <meta property="og:url" content="https://sabbafarm.com/orders" />
        <meta
          property="og:image"
          content="https://sabbafarm.com/assets/og-orders.png"
        />
      </Helmet>

      <div className="flex flex-col md:flex-row min-h-screen max-md:min-h-fit bg-site-bg p-2 sm:p-4 md:p-8">
        <aside
          className={`w-full md:w-64 bg-card-bg ${
            isFiltersContentVisible ? "p-3 md:p-6" : "px-3 pt-3 md:px-6 md:pt-6"
          } shadow-md md:shadow-lg rounded-lg md:rounded-l-lg mb-4 md:mb-0 md:mr-6 shrink-0 border border-green-border-light`}
        >
          <div className="flex justify-between items-center mb-4 pb-2 border-b border-green-border-light">
            <h3 className="text-xl font-bold text-gray-800">Filters</h3>
            <Button
              onClick={() =>
                setIsFiltersContentVisible(!isFiltersContentVisible)
              }
              className="md:hidden text-gray-600 hover:text-gray-800 min-w-0 p-1 py-2 rounded-full"
            >
              {isFiltersContentVisible ? (
                <FaChevronUp
                  style={{ color: "black" }}
                  className="md:hidden"
                  size={12}
                />
              ) : (
                <FaChevronDown
                  className="md:hidden"
                  size={12}
                  style={{ color: "black" }}
                />
              )}
            </Button>
          </div>

          <div
            className={`${
              isFiltersContentVisible ? "block" : "hidden md:block"
            }`}
          >
            <div className={`mb-3`}>
              <h4 className="text-lg font-semibold text-gray-700 mb-3">
                ORDER STATUS
              </h4>
              <div className="space-y-2">
                {backendOrderStatusEnum.map((status) => (
                  <label
                    key={status}
                    className="flex items-center cursor-pointer text-gray-700 hover:text-gray-900 transition-colors duration-150"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-brand-primary rounded-sm focus:ring-brand-primary border-gray-300 mr-2"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusChange(status)}
                    />
                    <span className="text-sm">{getDisplayStatus(status)}</span>
                  </label>
                ))}
                <label
                  key="Returned"
                  className="flex items-center cursor-pointer text-gray-700 hover:text-gray-900 transition-colors duration-150"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-brand-primary rounded-sm focus:ring-brand-primary border-gray-300 mr-2"
                    checked={selectedStatuses.includes("Returned")}
                    onChange={() => handleStatusChange("Returned")}
                  />
                  <span className="text-sm">Returned</span>
                </label>
              </div>
            </div>

            {/* Order Time Filter */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-700 my-3">
                ORDER TIME
              </h4>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer text-gray-700 hover:text-gray-900 transition-colors duration-150">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-brand-primary rounded-sm focus:ring-brand-primary border-gray-300 mr-2"
                    checked={isLast30DaysSelected}
                    onChange={handleLast30DaysChange}
                  />
                  <span className="text-sm">Last 30 days</span>
                </label>
                {/* {orderYearOptions.map((year) => (
                  <label
                    key={year}
                    className="flex items-center cursor-pointer text-gray-700 hover:text-gray-900 transition-colors duration-150"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-brand-primary rounded-sm focus:ring-brand-primary border-gray-300 mr-2"
                      checked={selectedYears.includes(year)}
                      onChange={() => handleYearChange(year)}
                      disabled={isLast30DaysSelected}
                    />
                    <span className="text-sm">{year}</span>
                  </label>
                ))} */}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-grow bg-card-bg p-3 md:p-6 rounded-lg shadow-md md:shadow-lg border border-green-border-light">
          {/* Search Bar */}
          <div className="mb-4 flex flex-col sm:flex-row items-center gap-4">
            <input
              type="text"
              placeholder="Search your orders here"
              className="flex-grow w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 text-gray-700"
              value={tempSearchTerm}
              onChange={(e) => setTempSearchTerm(e.target.value)}
            />
            <button
              className="bg-brand-primary max-md:hidden hover:bg-brand-primary-hover text-white font-semibold py-2 px-6 rounded-md transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto"
              onClick={handleSearchClick}
            >
              Search Orders
            </button>
          </div>

          {/* Orders List / Cards */}
          <div className="space-y-4 md:space-y-6">
            {ordersLoading ? (
              <>
                <OrderCardSkeleton />
                <OrderCardSkeleton />
                <OrderCardSkeleton />
              </>
            ) : Array.isArray(orders) && orders.length > 0 ? (
              orders.map((order) => (
                <div
                  key={order?._id}
                  className="border border-green-border-light rounded-lg p-3 md:p-6 shadow-sm bg-card-bg"
                >
                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-4 mb-4 border-b border-green-border-light">
                    <div className="mb-2 md:mb-0">
                      <h3 className="font-semibold text-gray-800 text-lg">
                        Order ID: {order?.orderId || order?._id}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Ordered on {formatDate(order?.date)}
                      </p>
                      {/* Payment Mode Information */}
                      {order?.paymentMode && (
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              order.paymentMode === "hybrid_cod"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {order.paymentMode === "hybrid_cod"
                              ? "🔄 Hybrid COD"
                              : "💳 Full Payment"}
                          </span>
                          {order.paymentMode === "hybrid_cod" &&
                            order.onlineAmount &&
                            order.codAmount && (
                              <div className="text-xs text-gray-600 mt-1">
                                <span className="text-green-600">
                                  Online: ₹{order.onlineAmount}
                                </span>
                                <span className="mx-2">•</span>
                                <span className="text-orange-600">
                                  COD: ₹{order.codAmount}
                                </span>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 rounded text-xs font-semibold uppercase min-w-[80px] text-center ${getStatusBadgeClasses(
                        order?.orderStatus
                      )}`}
                    >
                      {getDisplayStatus(order?.orderStatus)}
                    </span>
                  </div>

                  {/* Products in Order */}
                  <div className="space-y-4">
                    {order.products?.map((item) => (
                      <div
                        key={item.productId}
                        className="flex flex-col sm:flex-row gap-4 items-center max-md:items-start pb-4 last:border-b-0 last:pb-0"
                      >
                        <img
                          src={item.productDetails?.images?.[0]}
                          alt={item.productTitle}
                          onClick={() => navigate(`/product/${item.productId}`)}
                          className="w-24 h-24 object-cover rounded-md border border-green-border-light shrink-0"
                        />
                        <div
                          onClick={() => navigate(`/product/${item.productId}`)}
                          className="flex-grow"
                        >
                          <h4 className="font-semibold text-gray-800 text-base">
                            {item.productTitle}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Color: {item.productDetails?.color || "N/A"}
                          </p>
                          <p className="text-sm text-gray-600">
                            Quantity: {item.quantity}
                          </p>
                          <p className="text-lg font-bold text-gray-900 mt-1">
                            ₹{item.subTotal}
                          </p>
                        </div>
                        <div className="text-right h-full flex flex-col items-start pt-2 sm:pt-0">
                          {order.orderStatus === "delivered" && (
                            <p className="font-medium text-green-700 text-sm mb-1">
                              Delivered on {formatDate(order.date)}
                            </p>
                          )}

                          {/* Invoice Download Button */}
                          {order.invoice && (
                            <button
                              className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded-md text-xs transition-colors duration-200 flex items-center justify-center gap-1"
                              onClick={() => downloadInvoice(order._id)}
                            >
                              <FaDownload size={12} /> Download Invoice
                            </button>
                          )}

                          {/* View Details Button */}
                          <button
                            className="bg-brand-primary hover:bg-brand-primary-hover text-white font-medium py-2 px-3 rounded-md text-xs transition-colors duration-200 flex items-center justify-center gap-1 mb-2"
                            onClick={() => {
                              dispatch(setSelectedOrder(order));
                              setIsOpenModal(true);
                            }}
                          >
                            View Details
                          </button>

                          {order.orderStatus !== "cancelled" &&
                          order.orderStatus !== "delivered" ? (
                            <button
                              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-3 rounded-md text-xs transition-colors duration-200 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => cancelOrder(order._id)}
                              disabled={cancellingOrderId === order._id}
                            >
                              {cancellingOrderId === order._id ? (
                                <>
                                  <CircularProgress size={16} color="inherit" />{" "}
                                  Cancelling...
                                </>
                              ) : (
                                "Cancel Order"
                              )}
                            </button>
                          ) : order.orderStatus === "delivered" &&
                            !order.hasReviewed ? (
                            <button
                              className="bg-brand-primary hover:bg-brand-primary-hover text-white font-medium py-2 px-3 rounded-md text-xs transition-colors duration-200 flex items-center justify-center gap-1"
                              onClick={() =>
                                toast.info(
                                  "Rate & Review functionality coming soon!"
                                )
                              }
                            >
                              <FaStar /> Rate & Review Product
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Summary and Payment Info */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg font-semibold text-gray-800">
                            Total: ₹{order.amount}
                          </span>
                          {order.paymentMode && (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                order.paymentMode === "hybrid_cod"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {order.paymentMode === "hybrid_cod"
                                ? "🔄 Hybrid COD"
                                : "💳 Full Payment"}
                            </span>
                          )}
                        </div>
                        {order.paymentMode === "hybrid_cod" &&
                          order.onlineAmount &&
                          order.codAmount && (
                            <div className="text-sm text-gray-600">
                              <span className="text-green-600 font-medium">
                                Online Paid: ₹{order.onlineAmount}
                              </span>
                              <span className="mx-2">•</span>
                              <span className="text-orange-600 font-medium">
                                COD Due: ₹{order.codAmount}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  {order.orderStatus === "returned" ||
                  (order.orderStatus === "cancelled" && order.refundDetails) ? (
                    <div className="mt-4 p-4 bg-info-success-bg border border-info-success-border rounded-lg text-info-success-text">
                      <p className="font-semibold text-lg flex items-center gap-2 mb-2">
                        <FaInfoCircle className="text-info-success-text" />{" "}
                        Refund Completed
                      </p>
                      <p className="text-sm">
                        You returned this order because the quality was not as
                        expected.
                      </p>
                      {order.refundDetails && (
                        <p className="text-sm mt-2">
                          <span className="font-semibold">Refund ID:</span>{" "}
                          {order.refundDetails.razorpayRefundId || "N/A"}
                        </p>
                      )}
                      <p className="text-sm mt-1">
                        The money was sent to your STATE BANK OF INDIA Account
                        ending with ********080 on{" "}
                        {formatDate(order.refundDetails?.refundDate)}. For any
                        questions, please contact your bank with reference
                        number 520021263848.
                      </p>
                      {order.refundDetails && (
                        <button
                          onClick={() =>
                            handleOpenRefundModal(order.refundDetails)
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm mt-3 px-3 py-1 rounded hover:bg-blue-50 transition-colors duration-150"
                        >
                          View Full Refund Details
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No orders found matching your criteria.
              </div>
            )}
          </div>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={ordersPagination?.totalOrders || 0}
            page={ordersPage}
            onPageChange={handleOrdersPageChange}
            rowsPerPage={ordersRowsPerPage}
            onRowsPerPageChange={handleOrdersRowsPerPageChange}
            className="bg-card-bg rounded-lg shadow-md mt-4 py-1 pb-2 px-2 md:px-4 border border-green-border-light flex justify-end items-center flex-wrap"
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: "rgb(75 85 99)",
                  fontSize: "0.8rem",
                  "@media (max-width: 640px)": {
                    fontSize: "0.75rem",
                  },
                },
              "& .MuiTablePagination-toolbar": {
                justifyContent: "flex-end",
                gap: "4px",
                flexWrap: "wrap",
                minHeight: "40px",
                "@media (max-width: 640px)": {
                  gap: "2px",
                  minHeight: "36px",
                },
              },
              "& .MuiTablePagination-actions": {
                "@media (max-width: 640px)": {
                  marginTop: "0",
                  marginLeft: "4px",
                },
              },
              "& .MuiTablePagination-actions .MuiIconButton-root": {
                color: "rgb(51 51 51)",
                border: "1px solid rgb(229 231 235)",
                borderRadius: "4px",
                margin: "0 1px",
                padding: "6px",
                transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                "@media (max-width: 640px)": {
                  padding: "4px",
                  margin: "0 0.5px",
                },
                "&:hover": {
                  backgroundColor: "rgb(240 240 240)",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                },
              },
              "& .MuiTablePagination-select": {
                "@media (max-width: 640px)": {
                  fontSize: "0.75rem",
                },
              },
            }}
          />
        </main>
      </div>

      {/* Refund Details Section (if you still want a separate section for ALL refunds) */}
      {refunds && refunds.length > 0 && !refundsLoading && (
        <section className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto bg-card-bg p-4 md:p-6 rounded-lg shadow-md mb-8 lg:mb-12 border border-green-border-light">
            <h2 className="text-xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-green-border-light">
              Your Recent Refunds
            </h2>
            <div className="space-y-4">
              {refunds.map((refund) => (
                <div
                  key={refund._id}
                  className="border border-green-border-light rounded-lg p-4 shadow-sm bg-card-bg flex flex-col sm:flex-row justify-between items-start sm:items-center"
                >
                  <div>
                    <h3 className="font-semibold text-gray-800 text-base mb-1">
                      Refund ID: {refund.razorpayRefundId || "N/A"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Order ID:{" "}
                      {refund.orderId?.orderId || refund.orderId?._id || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Amount: ₹{refund.amount} {refund.currency}
                    </p>
                    <p className="text-sm text-gray-600">
                      Processed On: {formatDate(refund.refundDate)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end max-md:items-start mt-3 sm:mt-0">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold uppercase min-w-[70px] text-center ${getStatusBadgeClasses(
                        refund.status
                      )}`}
                    >
                      {getDisplayStatus(refund.status)}
                    </span>
                    <button
                      className="bg-brand-primary hover:bg-brand-primary-hover text-white font-medium py-2 px-4 rounded-md text-sm transition-colors duration-200 mt-3"
                      onClick={() => handleOpenRefundModal(refund)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <TablePagination
              component="div"
              count={refundsPagination?.totalRefunds || 0}
              page={refundsPage}
              onPageChange={handleRefundsPageChange}
              rowsPerPage={refundsRowsPerPage}
              onRowsPerPageChange={handleRefundsRowsPerPageChange}
              className="bg-card-bg rounded-lg shadow-md mt-4 py-1 pb-2 px-2 md:px-4 border border-green-border-light flex justify-end items-center flex-wrap"
              sx={{
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    color: "rgb(75 85 99)",
                    fontSize: "0.8rem",
                    "@media (max-width: 640px)": {
                      fontSize: "0.75rem",
                    },
                  },
                "& .MuiTablePagination-toolbar": {
                  justifyContent: "flex-end",
                  gap: "4px",
                  flexWrap: "wrap",
                  minHeight: "40px",
                  "@media (max-width: 640px)": {
                    gap: "2px",
                    minHeight: "36px",
                  },
                },
                "& .MuiTablePagination-actions": {
                  "@media (max-width: 640px)": {
                    marginTop: "0",
                    marginLeft: "4px",
                  },
                },
                "& .MuiTablePagination-actions .MuiIconButton-root": {
                  color: "rgb(51 51 51)",
                  border: "1px solid rgb(229 231 235)",
                  borderRadius: "4px",
                  margin: "0 1px",
                  padding: "6px",
                  transition:
                    "background-color 0.2s ease, box-shadow 0.2s ease",
                  "@media (max-width: 640px)": {
                    padding: "4px",
                    margin: "0 0.5px",
                  },
                  "&:hover": {
                    backgroundColor: "rgb(240 240 240)",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                  },
                },
                "& .MuiTablePagination-select": {
                  "@media (max-width: 640px)": {
                    fontSize: "0.75rem",
                  },
                },
              }}
            />
          </div>
        </section>
      )}

      {/* Order Details Modal */}
      <Dialog
        open={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className:
            "rounded-xl shadow-2xl p-4 md:p-6 relative w-full h-full md:w-auto md:h-auto", // Reduced padding
        }}
      >
        <div className="p-4 overflow-y-auto max-h-[80vh]">
          {selectedOrder ? (
            <>
              <h4 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
                Order Details
              </h4>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Order ID:
                </strong>{" "}
                {selectedOrder._id}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Name:
                </strong>{" "}
                {selectedOrder.name}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Phone:
                </strong>{" "}
                {selectedOrder.phoneNumber}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Address:
                </strong>{" "}
                {selectedOrder.address}
              </p>
              {/* Payment Information */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h5 className="text-lg font-semibold text-gray-800 mb-3">
                  Payment Details
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payment Mode</p>
                    <p className="font-semibold text-gray-800">
                      {selectedOrder.paymentMode === "hybrid_cod"
                        ? "🔄 Hybrid COD (5% Online + 95% COD)"
                        : "💳 Full Payment Online"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                    <p className="font-semibold text-gray-800 text-lg">
                      ₹{selectedOrder.amount}
                    </p>
                  </div>
                  {selectedOrder.paymentMode === "hybrid_cod" && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Online Payment (5%)
                        </p>
                        <p className="font-semibold text-green-600">
                          ₹
                          {selectedOrder.onlineAmount ||
                            Math.round(selectedOrder.amount * 0.05)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Cash on Delivery (95%)
                        </p>
                        <p className="font-semibold text-orange-600">
                          ₹
                          {selectedOrder.codAmount ||
                            Math.round(selectedOrder.amount * 0.95)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <p className="mb-2 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Status:
                </strong>{" "}
                {getDisplayStatus(selectedOrder.orderStatus)}{" "}
              </p>
              <p className="mb-4 text-gray-700">
                <strong className="min-w-[120px] inline-block font-semibold text-gray-900">
                  Date:
                </strong>{" "}
                {formatDate(selectedOrder.date)}
              </p>

              <hr className="my-6 border-t border-gray-200" />

              <h5 className="text-lg md:text-xl font-bold text-gray-800 mb-4">
                Products
              </h5>
              <div className="space-y-4">
                {selectedOrder.products?.map((item) => (
                  <div
                    key={item.productId}
                    className="border border-green-border-light rounded-lg p-3 bg-card-bg"
                  >
                    <div className="flex flex-col sm:flex-row gap-3 items-center sm:items-start">
                      <img
                        src={item.productDetails?.images?.[0]}
                        alt={item.productTitle || "Product Image"}
                        className="w-20 h-20 object-cover rounded-md border border-green-border-light shrink-0"
                      />
                      <div className="flex-grow">
                        <p className="font-semibold text-gray-800 text-base">
                          {item.productTitle}
                        </p>
                        <p className="text-sm text-gray-600">
                          Quantity: {item.quantity}
                        </p>
                        <p className="text-sm text-gray-600">
                          Subtotal: ₹{item.subTotal}
                        </p>
                        <div className="mt-2 text-sm">
                          {(item.productDetails?.prices || []).map((p, idx) => (
                            <div key={idx} className="flex gap-2">
                              <span className="font-medium text-gray-900">
                                ₹{p.actualPrice}
                              </span>
                              {p.oldPrice && (
                                <del className="text-gray-500">
                                  ₹{p.oldPrice}
                                </del>
                              )}
                              {p.discount && (
                                <span className="text-green-600">
                                  ({p.discount}%)
                                </span>
                              )}
                              <span className="text-gray-500">({p.type})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <CircularProgress size={48} className="text-gray-400" />
              <p className="mt-4 text-gray-600">Loading order details...</p>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={isRefundModalOpen}
        onClose={handleCloseRefundModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className:
            "rounded-xl shadow-2xl p-4 md:p-6 relative w-full h-full md:w-auto md:h-auto", // Reduced padding
        }}
      >
        <Button
          className="absolute top-4 right-4 text-gray-600 hover:bg-gray-100 hover:text-gray-800 rounded-full p-2 transition-colors duration-200 min-w-0"
          onClick={handleCloseRefundModal}
        >
          <MdClose size={24} />
        </Button>
        <div className="p-4 overflow-y-auto max-h-[80vh]">
          {selectedRefund ? (
            <>
              <h4 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
                Refund Details
              </h4>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Refund ID:
                </strong>{" "}
                {selectedRefund.id}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Razorpay Refund ID:
                </strong>{" "}
                {selectedRefund.razorpayRefundId || "N/A"}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Order ID:
                </strong>{" "}
                {selectedRefund.orderId?.orderId ||
                  selectedRefund.orderId?._id ||
                  "N/A"}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Order Date:
                </strong>{" "}
                {formatDate(selectedRefund.orderId?.date)}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Refund Amount:
                </strong>{" "}
                ₹{selectedRefund.amount} {selectedRefund.currency}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Status:
                </strong>{" "}
                {getDisplayStatus(selectedRefund.status)}{" "}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Reason:
                </strong>{" "}
                {selectedRefund.refundReason || "N/A"}
              </p>
              <p className="mb-4 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Refund Processed On:
                </strong>{" "}
                {formatDate(selectedRefund.refundDate)}
              </p>
              <hr className="my-6 border-t border-gray-200" />
              <h5 className="text-lg md:text-xl font-bold text-gray-800 mb-4">
                Payment Info
              </h5>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Payment ID:
                </strong>{" "}
                {selectedRefund.payment?.razorpayPaymentId || "N/A"}
              </p>
              <p className="mb-2 text-gray-700">
                <strong className="min-w-[180px] inline-block font-semibold text-gray-900">
                  Payment Order ID:
                </strong>{" "}
                {selectedRefund.payment?.razorpayOrderId || "N/A"}
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <CircularProgress size={48} className="text-gray-400" />
              <p className="mt-4 text-gray-600">Loading refund details...</p>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default Orders;
